// ResourceRelationships.tsx
import React, { useMemo } from 'react';
import ResourceRelationshipDiagram from './ResourceRelationshipDiagram';

export interface DataSourceItem {
    id: string;
    name: string;
    path?: string;
    type?: string;
    item_type?: string;
    source_type?: string;
    metadata?: {
        display_path?: string;
        file_count?: number;
        original_metadata?: {
            mime_type?: string;
            mimeType?: string;
            file_size?: number;
        };
        full_path?: string;
        path_tokens?: string[];
        folder_name?: string;
    };
    file_path?: string;
    description?: string;
    status?: string;
    is_root?: boolean;
    folder_id?: string;
}

export interface ResourceItem {
    id: string;
    name: string;
    type?: string;
    item_type?: string;
    data_source_ids?: string[];
    index_ids?: string[];
    description?: string;
    status?: string;
}

interface DataSource {
    id: string;
    name: string;
    type: string;
    item_type: string;
    source_type: string;
    metadata?: DataSourceItem['metadata'];
    description?: string;
    status?: string;
    file_path?: string;
    folder_id?: string;
}

interface Index {
    id: string;
    name: string;
    type: string;
    item_type: string;
    description?: string;
    status?: string;
    dataSourceIds: string[];
}

interface RagApplication {
    id: string;
    name: string;
    type: string;
    item_type: string;
    description?: string;
    status?: string;
    indexIds: string[];
}

interface ResourceData {
    dataSources: DataSource[];
    indexes: Index[];
    ragApplications: RagApplication[];
}

interface ResourceRelationshipsProps {
    data?: {
        dataSources?: DataSourceItem[] | null;
        indexes?: ResourceItem[] | null;
        ragApplications?: ResourceItem[] | null;
    };
    isLoading?: boolean;
    onRefresh?: () => void;
}

const ResourceRelationships: React.FC<ResourceRelationshipsProps> = ({
                                                                         data,
                                                                         isLoading = false
                                                                     }) => {
    // Ensure data and its properties are arrays, even if they come back as null/undefined
    const safeData = useMemo(() => {
        // First ensure data object exists
        const safeDataObj = data || {};

        // Then ensure each property is an array
        return {
            dataSources: Array.isArray(safeDataObj.dataSources) ? safeDataObj.dataSources : [],
            indexes: Array.isArray(safeDataObj.indexes) ? safeDataObj.indexes : [],
            ragApplications: Array.isArray(safeDataObj.ragApplications) ? safeDataObj.ragApplications : []
        };
    }, [data]);

    // Transform the data for the diagram using useMemo for performance
    const diagramData = useMemo<ResourceData>(() => {
        const transformedData: ResourceData = {
            dataSources: safeData.dataSources
                .filter((doc): doc is DataSourceItem => {
                    if (!doc || typeof doc !== 'object') return false;

                    // Exclude folders but include all valid source types
                    const isFolder = doc.item_type === 'folder' ||
                        doc.type === 'folder' ||
                        doc.metadata?.file_count !== undefined;

                    const isValidSource = doc.source_type === 'file_upload' ||
                        doc.source_type === 'web_source' ||
                        doc.source_type === 'api_source' ||
                        doc.source_type === 'drive_sync' ||
                        doc.item_type === 'file';

                    return !isFolder && isValidSource;
                })
                .map(doc => {
                    // Get the full path, handling different path formats
                    let displayPath = '';
                    if (doc?.metadata?.full_path) {
                        const folderPath = doc.metadata.full_path.replace(/^Home\/?/, '');
                        displayPath = folderPath ? `${folderPath}/${doc.name}` : doc.name;
                    } else {
                        displayPath = doc.file_path || doc.metadata?.display_path || doc.path || doc.name || '';
                    }
                    displayPath = displayPath.replace(/^Home\/?/, '').replace(/\/+/g, '/');

                    return {
                        id: doc.id,
                        name: displayPath || doc.name,
                        type: doc.type || 'file',
                        item_type: doc.item_type || 'file',
                        source_type: doc.source_type || 'file_upload',
                        metadata: doc.metadata,
                        description: doc.description,
                        status: doc.status,
                        file_path: displayPath,
                        folder_id: doc.folder_id
                    };
                }),
            indexes: safeData.indexes
                .filter((idx): idx is ResourceItem => {
                    return idx !== null && typeof idx === 'object' && 'id' in idx && 'name' in idx;
                })
                .map(idx => ({
                    id: idx.id,
                    name: idx.name,
                    type: 'index',
                    item_type: 'index',
                    description: idx.description,
                    status: idx.status,
                    dataSourceIds: Array.isArray(idx.data_source_ids) ? idx.data_source_ids : []
                })),
            ragApplications: safeData.ragApplications
                .filter((app): app is ResourceItem => {
                    return app !== null && typeof app === 'object' && 'id' in app && 'name' in app;
                })
                .map(app => ({
                    id: app.id,
                    name: app.name,
                    type: 'rag',
                    item_type: 'rag',
                    description: app.description,
                    status: app.status,
                    indexIds: Array.isArray(app.index_ids) ? app.index_ids : []
                }))
        };

        return transformedData;
    }, [safeData]);

    if (isLoading) {
        return (
            <div className="border-0">
                <div className="d-flex justify-content-center align-items-center" style={{ height: '16rem' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    const hasNoResources = (
        diagramData.dataSources.length === 0 &&
        diagramData.indexes.length === 0 &&
        diagramData.ragApplications.length === 0
    );

    return (
        <div className="bg-transparent">
            <style>{`
                .resource-item {
                    border: 1px solid #dee2e6 !important;
                    background-color: #f8f9fa;
                    border-radius: 0.25rem;
                }
                
                .resource-relationships > div,
                .card,
                .card-body {
                    border: 0;
                    box-shadow: none;
                }
                
                .resource-group {
                    border: 1px solid #dee2e6;
                    border-radius: 0.25rem;
                    padding: 1rem;
                }
            `}</style>
            <div className="resource-relationships">
                {hasNoResources ? (
                    <div className="text-center p-4">
                        <p className="text-muted">
                            No resources found. Please add some data sources, knowledge bases, or RAG applications.
                        </p>
                    </div>
                ) : (
                    <ResourceRelationshipDiagram data={diagramData} />
                )}
            </div>
        </div>
    );
};

export default ResourceRelationships;